import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { forwardRef } from 'react';
import type { IconProps } from '@anyroad/icon';
import { Icon } from '@anyroad/icon';
import styled from '@emotion/styled';
import { Spinner } from '@anyroad/spinner';
import { fontSizes, fontWeights } from '@anyroad/theme';
import {
  handleBackgroundColor,
  handleBorderColor,
  handleColor,
  handleHoverColor,
} from './helpers';

type colorEnum =
  | 'destructive'
  | 'primary'
  | 'secondary'
  | 'subtle'
  | 'tertiary';

type ButtonStyleTypes = {
  color?: colorEnum;
  compact?: boolean;
  disabled?: boolean;
  height?: string;
  loading?: boolean;
  padding?: string;
  width?: string;
};

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  ButtonStyleTypes & {
    children: ReactNode;
  };

export const StyledBasicButton = styled.button<ButtonStyleTypes>`
  background-color: ${({ color }) => handleBackgroundColor(color)};
  border: 1px solid ${({ color }) => handleBorderColor(color)};
  border-radius: 4px;
  color: ${({ color }) => handleColor(color)};
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
  font-size: ${fontSizes.large};
  font-weight: ${fontWeights.regular};
  opacity: ${(props) => (props.disabled ? '0.5' : '1')};
  padding: ${(props) =>
    props.padding ? props.padding : props.compact ? '8px 32px' : '12px 32px'};

  width: ${(props) => (props.width ? props.width : 'initial')};
  height: ${(props) => (props.height ? props.height : 'initial')};

  &:hover {
    background-color: ${({ disabled, color }) =>
      disabled ? handleBackgroundColor(color) : handleHoverColor(color)};
  }

  @media (max-width: 768px) {
    font-size: ${fontSizes.mobile};
  }
`;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ children, disabled, compact, color, loading = false, ...rest }, ref) => (
    <StyledBasicButton
      color={color}
      compact={compact}
      disabled={disabled}
      ref={ref}
      {...rest}
    >
      {loading ? <Spinner height={16} type="ring" width={16} /> : children}
    </StyledBasicButton>
  )
);

Button.displayName = 'Button';

type IconButtonProps = ButtonProps & {
  color?: colorEnum;
  iconName: IconProps['name'];
};

const StyledIcon = styled(Icon)`
  height: ${fontSizes.large};
  width: ${fontSizes.large};
  margin-right: 8px;

  @media (max-width: 768px) {
    height: ${fontSizes.mobile};
    width: ${fontSizes.mobile};
  }
`;

const StyledIconButton = styled(StyledBasicButton)`
  padding: ${(props) => (props.compact ? '8px 16px' : '12px 16px')};
`;

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ iconName, children, color, ...rest }, ref) => (
    <StyledIconButton color={color} ref={ref} {...rest}>
      <StyledIcon fill={handleColor(color)} name={iconName} />
      {children}
    </StyledIconButton>
  )
);

IconButton.displayName = 'IconButton';

type IconCircleButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  color?: colorEnum;
  iconName: IconProps['name'];
};

const StyledIconCircleButton = styled(StyledBasicButton)`
  border-radius: 50%;
  line-height: 12px;
  padding: 12px;
  svg {
    margin: 0;
    height: 100%;
    width: 100%;
  }
`;

export const IconCircleButton = forwardRef<
  HTMLButtonElement,
  IconCircleButtonProps
>(({ iconName, color, ...rest }, ref) => (
  <StyledIconCircleButton color={color} ref={ref} {...rest}>
    <StyledIcon fill={handleColor(color)} name={iconName} />
  </StyledIconCircleButton>
));

IconCircleButton.displayName = 'IconCircleButton';
